"use client";
import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { useAppSelector } from "../../../redux/typedHooks";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";
import { getUserType } from "../../../utils/getUserType";

import type { UserInterface } from "../../../types/user";
import {
  PersonalInformation,
  Billing,
  Notifications,
  ChangePassword,
  PrivacySettings,
  CustomContentPricing,
  TipMinimums,
  WelcomeMessage,
  BlackList,
  Payounts,
  SubscriptionTierPricing,
  Monetization,
} from "./index";
import Logo from "../../../assets/Logo.svg";
import PersonalInformationIcon from "../../../assets/svgs/PersonalInformationIcon";
import BillingIcon from "../../../assets/svgs/BillingIcon";
import NotificationsIcon from "../../../assets/svgs/NotificationsIcon";
import { ReactComponent as WelcomeMessageIcon } from "../../../assets/svgs/welcome_message.svg"
import PasswordIcon from "../../../assets/svgs/PasswordIcon";
import { useSettingsStyles } from "./style";
import MonitorChatIcon from "../../../assets/payountsIcon";
import { DollarSign, Shield } from "lucide-react";
import { useLocation } from "react-router-dom";
import { SvgIconComponent } from "@material-ui/icons";
// DIDIT INTEGRATION - COMMENTED OUT
// import DiditVerification from "../../elements/DiditVerification";

type SettingsSection =
  | "personal"
  | "billing"
  | "payounts"
  | "notifications"
  | "passwordAndTwoFA"
  | "privacy"
  | "payPerView"
  | "customContent"
  | "tipMinimums"
  | "welcomeMessage"
  | "BlackList"
  | "subscriptionPricing"
  // DIDIT INTEGRATION - COMMENTED OUT
  // | "verification"
  | "earningsHistory"
  | "monetization";

type Item = {
  id: string;
  label: string;
  icon?: SvgIconComponent;
};

const Settings: React.FC = () => {
  const classes = useSettingsStyles();
  const seo = useSEO("settings");
  const location = useLocation<{ activeSection?: SettingsSection }>();

  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );

  const userType = getUserType(user);
  const isViewer = userType?.title?.toLowerCase() === "viewer";

  const [activeSection, setActiveSection] = useState<SettingsSection>(() => {
    if (location.state?.activeSection) return location.state.activeSection;
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab") as SettingsSection;
    if (tabParam) return tabParam;
    return "personal";
  });

  React.useEffect(() => {
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection);
    } else {
      const params = new URLSearchParams(location.search);
      const tabParam = params.get("tab") as SettingsSection;
      if (tabParam) {
        setActiveSection(tabParam);
      }
    }
  }, [location.state, location.search]);

  const [formState] = useState({
    name: "Creator",
    username: "Creator",
    website: "",
    bio: "",
    email: user?.email || "",
    phone: "",
    gender: "Female",
    allowMessages: true,
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleSave = () => { };

  const handleEarningsSectionClick = (id: SettingsSection) => {
    setActiveSection(id);
  };

  const menuItems = [
    {
      id: "personal",
      label: "Personal info",
      icon: PersonalInformationIcon,
      isForViewer: true
    },
    { id: "welcomeMessage", label: "Welcome messages", icon: WelcomeMessageIcon, isForViewer: false },
    {
      id: "monetization",
      label: "Monetization",
      icon: DollarSign,
      isForViewer: false
    },
    { id: "privacy", label: "Profile Privacy", icon: Shield, isForViewer: true },
  ];

  const notificatonItems = [
    { id: "notifications", label: "Notifications", icon: NotificationsIcon },
  ]

  const paymentsItems = [
    { id: "billing", label: "Payment methods", icon: BillingIcon, isForViewer: true },
    { id: "payounts", label: "Payout Management", icon: MonitorChatIcon, isForViewer: false },
  ]

  // const earningsMenuItems: Item[] = [
  //   //   {id: "payPerView", label: "Pay-per-view rates" },
  //   { id: "subscriptionPricing", label: "Subscription Pricing" },
  //   { id: "customContent", label: "Custom content pricing" },
  //   { id: "tipMinimums", label: "Tip minimums" },
  //   // {id: "BlackList", label: "Black list" },
  //   //  {id: "earningsHistory", label: "Earnings history and payouts" },
  // ];
  const menuItemsSecurity = [
    { id: "passwordAndTwoFA", label: "Password and 2FA", icon: PasswordIcon },
    // { id: "BlackList", label: "Black list", icon: NotificationsIcon },
    // DIDIT INTEGRATION - COMMENTED OUT
    // {
    //   id: "verification",
    //   label: "Identity Verification",
    //   icon: NotificationsIcon,
    // },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "personal":
        return (
          <PersonalInformation
          // user={user}
          />
        );

      case "billing":
        return <Billing handleSave={handleSave} />;
      case "payounts":
        return (
          <Payounts
          // user={user}
          />
        );
      case "notifications":
        return <Notifications />;

      case "passwordAndTwoFA":
        return <ChangePassword />;

      case "privacy":
        return <PrivacySettings />;

      //      case "payPerView":
      //       return <PayPerViewRates />;

      case "subscriptionPricing":
        return <SubscriptionTierPricing />;

      case "monetization":
        return <Monetization />;

      case "customContent":
        return <CustomContentPricing />;

      case "tipMinimums":
        return <TipMinimums />;

      case "welcomeMessage":
        return <WelcomeMessage />;
      case "BlackList":
        return <BlackList />;
      // DIDIT INTEGRATION - COMMENTED OUT
      // case "verification":
      //   return (
      //     <Box p={3}>
      //       <Typography variant="h5" gutterBottom>
      //         Identity Verification
      //       </Typography>
      //       <Typography variant="body1" style={{ marginBottom: 20 }}>
      //         Verify your identity to access premium features and increase your
      //         account security. Verification will start automatically.
      //       </Typography>
      //       <DiditVerification
      //         userId={user?.id || ""}
      //         embedded={true}
      //         autoStart={true}
      //         onVerificationComplete={result => {
      //           console.log("Verification completed:", result);
      //         }}
      //         onVerificationError={error => {
      //           console.error("Verification failed:", error);
      //         }}
      //       />
      //     </Box>
      //   );

      //      case "earningsHistory":
      //        return <EarningsHistory />;

      default: return null;
    }
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Box className={classes.root}>
        <Box className={classes.pageWrapper}>
          <Typography className={classes.sidebarTitle}>Settings</Typography>
          <Box className={classes.mainContainer}>
            <Box className={classes.sideMenu}>
              <Box className={classes.logoContainer}>
                <img
                  src={Logo || "/placeholder.svg"}
                  alt="SugarCribs Logo"
                  className={classes.logo}
                />
              </Box>
              <List component="nav" className={classes.sidebarWrapper}>
                <List component="nav">
                  <Typography className={classes.credentialsHeader}>
                    My profile
                  </Typography>
                  <List component="nav" disablePadding className={classes.navbar}>
                    {menuItems.map(item => ((isViewer && item.isForViewer) || !isViewer) ? (
                      <ListItem
                        button
                        key={item.id}
                        onClick={() => setActiveSection(item.id as SettingsSection)}
                        className={`${classes.sideMenuItem} ${classes.hoverEffect} ${activeSection === item.id ? classes.sideMenuItemActive : ""
                          }`}
                        disableGutters
                      >
                        {item.icon && (
                          <item.icon
                            className={`${classes.icon} ${activeSection === item.id ? classes.activeIcon : ""
                              }`}
                          />
                        )}
                        <ListItemText
                          primary={
                            <span
                              className={`${classes.label} ${activeSection === item.id ? classes.activeText : ""
                                }`}
                            >
                              {item.label}
                            </span>
                          }
                        />
                      </ListItem>
                    ) : null)}
                  </List>
                </List>
                <List component="nav">
                  <Typography className={classes.credentialsHeader}>
                    Payments
                  </Typography>
                  <List component="nav" disablePadding className={classes.navbar}>
                    {paymentsItems.map(item => ((isViewer && item.isForViewer) || !isViewer) ? (
                      <ListItem
                        button
                        key={item.id}
                        onClick={() => setActiveSection(item.id as SettingsSection)}
                        className={`${classes.sideMenuItem} ${classes.hoverEffect} ${activeSection === item.id ? classes.sideMenuItemActive : ""
                          }`}
                        disableGutters
                      >
                        {item.icon && (
                          <item.icon
                            className={`${classes.icon} ${activeSection === item.id ? classes.activeIcon : ""
                              }`}
                          />
                        )}
                        <ListItemText
                          primary={
                            <span
                              className={`${classes.label} ${activeSection === item.id ? classes.activeText : ""
                                }`}
                            >
                              {item.label}
                            </span>
                          }
                        />
                      </ListItem>
                    ) : null)}
                  </List>
                </List>
                {/* {!isViewer && (
                <List component="nav">
                  <Typography className={classes.credentialsHeader}>
                    Earnings & Pricing
                  </Typography>
                  <List component="nav" disablePadding className={classes.navbar}>
                    {earningsMenuItems.map(item => (
                      <ListItem
                        button
                        key={item.id}
                        onClick={() =>
                          handleEarningsSectionClick(item.id as SettingsSection)
                        }
                        className={`${classes.sideMenuItem} ${classes.hoverEffect} ${activeSection === item.id ? classes.sideMenuItemActive : ""
                          }`}
                        disableGutters
                      >
                        {item.icon && (
                          <item.icon
                            className={`${classes.icon} ${activeSection === item.id ? classes.activeIcon : ""
                              }`}
                          />
                        )}
                        <ListItemText
                          primary={
                            <span
                              className={`${classes.label} ${activeSection === item.id
                                ? classes.activeText
                                : ""
                                }`}
                            >
                              {item.label}
                            </span>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </List>
              )} */}
                <List component="nav">
                  <Typography className={classes.credentialsHeader}>
                    Security
                  </Typography>
                  <List component="nav" disablePadding className={classes.navbar}>
                    {menuItemsSecurity.map(item => (
                      <ListItem
                        button
                        key={item.id}
                        onClick={() => setActiveSection(item.id as SettingsSection)}
                        className={`${classes.sideMenuItem} ${classes.hoverEffect} ${activeSection === item.id ? classes.sideMenuItemActive : ""
                          }`}
                        disableGutters
                      >
                        {item.icon && (
                          <item.icon
                            className={`${classes.icon} ${activeSection === item.id ? classes.activeIcon : ""
                              }`}
                          />
                        )}
                        <ListItemText
                          primary={
                            <span
                              className={`${classes.label} ${activeSection === item.id ? classes.activeText : ""
                                }`}
                            >
                              {item.label}
                            </span>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </List>
                <List component="nav">
                  <List component="nav" disablePadding className={classes.navbar}>
                    {notificatonItems.map(item => (
                      <ListItem
                        button
                        key={item.id}
                        onClick={() => setActiveSection(item.id as SettingsSection)}
                        className={`${classes.sideMenuItem} ${classes.hoverEffect} ${activeSection === item.id ? classes.sideMenuItemActive : ""
                          }`}
                        disableGutters
                      >
                        {item.icon && (
                          <item.icon
                            className={`${classes.icon} ${activeSection === item.id ? classes.activeIcon : ""
                              }`}
                          />
                        )}
                        <ListItemText
                          primary={
                            <span
                              className={`${classes.label} ${activeSection === item.id ? classes.activeText : ""
                                }`}
                            >
                              {item.label}
                            </span>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </List>
              </List>
              <Box className={classes.footerContainer}>
                <Typography className={classes.footerText}>
                  Temporarily deactivate my account
                </Typography>
              </Box>
            </Box>
            <Box className={classes.contentContainer}>{renderContent()}</Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Settings;
